"use client";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Card, Col, Input, Row, Space, Tag, Typography, Alert, Spin } from "antd";
import { AuditOutlined, LockOutlined, MailOutlined, SafetyCertificateOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useRouter } from "next/navigation";

const { Text, Title } = Typography;
const LABEL: React.CSSProperties = { fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b" };

const loginSchema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function EmployeeLoginPage() {
  const router = useRouter();
  const [serverError, setServerError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string>();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  React.useEffect(() => {
    fetch("/api/auth/csrf")
      .then((r) => r.json())
      .then((d) => setCsrfToken(d.csrf_token ?? ""))
      .catch(() => {});
  }, []);

  const onSubmit = async (values: LoginFormValues) => {
    setServerError(null);
    setIsLoading(true);
    try {
      const res = await fetch("/api/auth/employee/login", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-csrf-token": csrfToken ?? "" },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403 && data.mfa_required) {
          setServerError("Multi-factor authentication is required. Please use your authenticator app.");
          return;
        }
        if (res.status === 429) {
          setServerError("Too many login attempts. Please wait and try again.");
          return;
        }
        setServerError(data.error || "Invalid credentials. Please check your email and password.");
        return;
      }
      router.push("/dashboard");
      router.refresh();
    } catch {
      setServerError("Unable to connect to the authentication service. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", padding: 24, background: "#edf0f7" }}>
      <Row gutter={[24, 24]} align="middle" style={{ width: "100%", maxWidth: 1040, margin: "0 auto" }}>
        <Col xs={24} lg={13}>
          <Space orientation="vertical" size={16}>
            <Tag color="blue" style={{ width: "fit-content" }}>Employee access only</Tag>
            <div>
              <Title style={{ margin: 0, color: "#1a2535" }}>Mechagora Employee Portal</Title>
              <Text style={{ fontSize: 16, color: "#64748b" }}>Operational workspace for broker routing, risk controls, SOC2 evidence, and identity governance.</Text>
            </div>
            <Row gutter={[12, 12]}>
              {[
                { icon: <ThunderboltOutlined />, title: "Operations", body: "Service readiness and incident signals" },
                { icon: <SafetyCertificateOutlined />, title: "Controls", body: "Role-aware risk and broker actions" },
                { icon: <AuditOutlined />, title: "SOC2", body: "Evidence and audit follow-up" },
              ].map((item) => (
                <Col xs={24} sm={8} key={item.title}>
                  <Card style={{ borderRadius: 4, height: "100%" }} styles={{ body: { padding: 14 } }}>
                    <div style={{ color: "#2563eb", fontSize: 18 }}>{item.icon}</div>
                    <Text strong style={{ display: "block", marginTop: 8 }}>{item.title}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{item.body}</Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </Space>
        </Col>

        <Col xs={24} lg={11}>
          <Card style={{ borderRadius: 4, boxShadow: "0 8px 24px rgba(15,23,42,0.08)" }}>
            <Space orientation="vertical" size={16} style={{ width: "100%" }}>
              <div style={{ width: 44, height: 44, borderRadius: 4, background: "#003087", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                <LockOutlined />
              </div>
              <div>
                <Text style={LABEL}>Secure sign in</Text>
                <Title level={3} style={{ marginTop: 4, marginBottom: 0 }}>Employee Authentication</Title>
                <Text type="secondary">Sign in with your Mechagora employee credentials. Access is governed by MFA, role scope, and clearance policy.</Text>
              </div>

              {serverError && (
                <Alert type="error" showIcon title={serverError} closable onClose={() => setServerError(null)} />
              )}

              <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
                <Space orientation="vertical" size={12} style={{ width: "100%" }}>
                  <div>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          size="large"
                          autoComplete="email"
                          placeholder="Email address"
                          prefix={<MailOutlined style={{ color: "#94a3b8" }} />}
                          status={errors.email ? "error" : undefined}
                          disabled={isLoading}
                        />
                      )}
                    />
                    {errors.email && <Text type="danger" style={{ fontSize: 12 }}>{errors.email.message}</Text>}
                  </div>
                  <div>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <Input.Password
                          {...field}
                          size="large"
                          autoComplete="current-password"
                          placeholder="Password"
                          prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                          status={errors.password ? "error" : undefined}
                          disabled={isLoading}
                        />
                      )}
                    />
                    {errors.password && <Text type="danger" style={{ fontSize: 12 }}>{errors.password.message}</Text>}
                  </div>
                  <Button type="primary" size="large" htmlType="submit" block disabled={isLoading}>
                    {isLoading ? <><Spin size="small" style={{ marginRight: 8 }} /> Signing in...</> : "Sign in"}
                  </Button>
                </Space>
              </form>

              <Text type="secondary" style={{ fontSize: 12 }}>
                Use approved employee identity only. All activity is logged for security and compliance review.
              </Text>
              <div style={{ textAlign: "center", marginTop: 4 }}>
                <a href="/api/auth/zitadel/employee/start" style={{ fontSize: 11, color: "#94a3b8", textDecoration: "underline" }}>
                  Continue with Zitadel instead
                </a>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
